import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";
import { Sparkles, Edit3, Trash2, Check, X, Plus } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_NOTE = {
  id: "note_01",
  content:
    "Yo minna! Vote proyek udah dibuka khusus Hardcore Cultist. Kalian yang nentuin game apa yang bakal gw garap duluan. Drop alasan pilihan kalian di kolom diskusi bawah ya! 🎮🔥",
  reactions: { fire: 42, love: 28, rocket: 19, skull: 7 },
  created_at: "Hari ini",
};

export default function FounderNote() {
  const { user } = useAuth();
  const isFounder = user?.role === "admin";

  const [note, setNote] = useState(() => {
    try {
      const saved = localStorage.getItem("kyouka_founder_note_v1");
      if (saved === "null") return null;
      return saved ? JSON.parse(saved) : DEFAULT_NOTE;
    } catch {
      return DEFAULT_NOTE;
    }
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState("");
  const [reacting, setReacting] = useState(false);

  const saveNoteState = (newNote) => {
    setNote(newNote);
    try {
      localStorage.setItem(
        "kyouka_founder_note_v1",
        newNote ? JSON.stringify(newNote) : "null"
      );
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const syncNote = async () => {
      try {
        const { data } = await api.get("/community/founder-note");
        if (cancelled) return;

        // One-time migration from Founder's PC localStorage if not yet migrated
        const migrated = localStorage.getItem("kyouka_note_migrated_v1");
        if (isFounder && !migrated && !data?.customized) {
          const local = localStorage.getItem("kyouka_founder_note_v1");
          if (local === "null") {
            await api.put("/community/founder-note", { hidden: true });
            localStorage.setItem("kyouka_note_migrated_v1", "1");
            if (!cancelled) saveNoteState(null);
            return;
          }
          if (local) {
            try {
              const parsed = JSON.parse(local);
              if (parsed?.content && parsed.content !== DEFAULT_NOTE.content) {
                const res = await api.put("/community/founder-note", {
                  content: parsed.content,
                  hidden: false,
                });
                localStorage.setItem("kyouka_note_migrated_v1", "1");
                if (!cancelled && res.data?.note) saveNoteState(res.data.note);
                return;
              }
            } catch {
              // ignore malformed local
            }
          }
        }

        if (data?.customized) {
          localStorage.setItem("kyouka_note_migrated_v1", "1");
          if (data.note?.hidden) {
            saveNoteState(null);
          } else if (data.note) {
            saveNoteState(data.note);
          }
        } else if (data?.note) {
          setNote(data.note);
        }
      } catch (err) {
        // silent fallback to localStorage / default
      }
    };

    syncNote();
    const timer = setInterval(syncNote, 25000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isFounder]);

  const handleStartEdit = () => {
    setEditContent(note?.content || "");
    setIsEditing(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editContent.trim()) {
      toast.info("Pesan pengumuman tidak boleh kosong.");
      return;
    }
    const updated = {
      ...(note || DEFAULT_NOTE),
      id: "founder_note_active",
      content: editContent.trim(),
      hidden: false,
      created_at: "Baru saja",
    };
    saveNoteState(updated);
    setIsEditing(false);

    try {
      const { data } = await api.put("/community/founder-note", {
        content: editContent.trim(),
        hidden: false,
      });
      localStorage.setItem("kyouka_note_migrated_v1", "1");
      if (data?.note) saveNoteState(data.note);
      toast.success("Pesan Founder berhasil diperbarui & sinkron di semua perangkat! 🎉");
    } catch (err) {
      toast.error("Gagal menyimpan ke server, coba lagi.");
    }
  };

  const handleDeleteNote = async () => {
    if (window.confirm("Hapus atau sembunyikan pesan pengumuman Founder ini dari beranda?")) {
      saveNoteState(null);
      setIsEditing(false);
      try {
        await api.put("/community/founder-note", { hidden: true });
        localStorage.setItem("kyouka_note_migrated_v1", "1");
        toast.success("Pesan Founder berhasil disembunyikan di semua perangkat. 🗑️");
      } catch (err) {
        toast.error("Gagal menyinkronkan ke server.");
      }
    }
  };

  const handleReact = async (type) => {
    if (!user) {
      toast.info("Silakan login untuk memberikan reaksi ke Founder.");
      return;
    }
    if (reacting) return;
    setReacting(true);

    const updatedReactions = {
      ...(note?.reactions || {}),
      [type]: ((note?.reactions || {})[type] || 0) + 1,
    };
    const updatedNote = { ...note, reactions: updatedReactions };
    saveNoteState(updatedNote);

    try {
      await api.post("/voting/founder-note/react", { reaction_type: type });
      toast.success("Reaksi terkirim ke Founder! ✨");
    } catch (e) {
      // silent
    } finally {
      setReacting(false);
    }
  };

  // If hidden/deleted: show create bar ONLY for Founder
  if (!note) {
    if (!isFounder) return null;
    return (
      <div className="flex items-center justify-between p-3.5 rounded-2xl bg-zinc-950/60 border border-dashed border-zinc-800 hover:border-amber-500/40 transition-all mb-8 text-xs text-zinc-400">
        <span className="flex items-center gap-2">
          <span>👑</span>
          <span>Pesan Pengumuman Founder sedang disembunyikan.</span>
        </span>
        <button
          type="button"
          onClick={() => {
            setEditContent("");
            setIsEditing(true);
            saveNoteState(DEFAULT_NOTE);
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-amber-300 font-bold transition active:scale-95"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>+ Tulis Pesan Pengumuman 👑</span>
        </button>
      </div>
    );
  }

  const reactions = note.reactions || { fire: 0, love: 0, rocket: 0, skull: 0 };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950 border border-rose-500/30 p-4 sm:p-5 shadow-xl shadow-rose-950/20 backdrop-blur-md mb-8">
      {/* Decorative background aura */}
      <div className="absolute top-0 right-0 w-64 h-full bg-rose-600/5 blur-3xl pointer-events-none" />

      <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        {/* Left: Founder profile and message */}
        <div className="flex items-start gap-3.5 min-w-0 flex-1 w-full">
          <div className="relative shrink-0 mt-0.5">
            <Avatar
              user={{
                role: "admin",
                name: note?.author_name || "Kyouka Reimei",
                avatar_url: note?.founder_avatar_url || (isFounder ? user?.avatar_url : ""),
                badges: ["cult_master"],
              }}
              size={42}
            />
            <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-rose-600 border-2 border-zinc-950 grid place-items-center text-[8px] font-bold text-white shadow">
              👑
            </span>
          </div>

          <div className="space-y-1.5 min-w-0 flex-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs sm:text-sm font-extrabold text-white">
                  Kyouka Reimei
                </span>
                <span className="badge-founder text-[10px]">👑 FOUNDER</span>
                <span className="badge-cult-master text-[10px]">🩸 CULT MASTER</span>
                <span className="text-[10px] text-zinc-500 hidden sm:inline">
                  • Update Status Proyek
                </span>
              </div>

              {/* Founder Controls (Edit & Delete) */}
              {isFounder && !isEditing && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleStartEdit}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 text-[11px] font-semibold transition"
                    title="Ubah teks pengumuman"
                  >
                    <Edit3 className="w-3 h-3" />
                    <span>Edit Pesan</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteNote}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-300 text-[11px] font-semibold transition"
                    title="Hapus / Sembunyikan pesan ini"
                  >
                    <Trash2 className="w-3 h-3" />
                    <span>Hapus</span>
                  </button>
                </div>
              )}
            </div>

            {/* Content or Edit Form */}
            {isEditing ? (
              <form onSubmit={handleSaveEdit} className="space-y-2.5 pt-1">
                <textarea
                  rows={3}
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  placeholder="Ketik pesan status pengumuman baru untuk member..."
                  className="w-full text-xs sm:text-sm bg-zinc-950 border border-amber-500/50 rounded-xl p-3 text-white focus:outline-none focus:ring-1 focus:ring-amber-400 resize-none"
                  autoFocus
                />
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="px-3 py-1.5 rounded-lg text-xs text-zinc-400 hover:text-white"
                  >
                    Batal
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs shadow-md transition"
                  >
                    <Check className="w-3.5 h-3.5" />
                    <span>Simpan Pesan</span>
                  </button>
                </div>
              </form>
            ) : (
              <p className="text-xs sm:text-sm text-zinc-300 leading-relaxed max-w-2xl">
                {note.content}
              </p>
            )}
          </div>
        </div>

        {/* Right: Quick emoji reaction buttons */}
        {!isEditing && (
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 w-full md:w-auto justify-end pt-2 md:pt-0 border-t md:border-t-0 border-zinc-800/80">
            <button
              type="button"
              onClick={() => handleReact("fire")}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-zinc-900/80 hover:bg-zinc-800 border border-zinc-800 hover:border-amber-500/40 text-xs text-zinc-300 hover:text-amber-300 transition-all active:scale-95 group"
              title="Beri Semangat 🔥"
            >
              <span className="group-hover:scale-125 transition-transform">🔥</span>
              <span className="font-semibold text-[11px] text-zinc-400 group-hover:text-amber-200">
                {reactions.fire || 0}
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleReact("love")}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-zinc-900/80 hover:bg-zinc-800 border border-zinc-800 hover:border-rose-500/40 text-xs text-zinc-300 hover:text-rose-300 transition-all active:scale-95 group"
              title="Dukung Founder ❤️"
            >
              <span className="group-hover:scale-125 transition-transform">❤️</span>
              <span className="font-semibold text-[11px] text-zinc-400 group-hover:text-rose-200">
                {reactions.love || 0}
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleReact("rocket")}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-zinc-900/80 hover:bg-zinc-800 border border-zinc-800 hover:border-cyan-500/40 text-xs text-zinc-300 hover:text-cyan-300 transition-all active:scale-95 group"
              title="Gaskeun! 🚀"
            >
              <span className="group-hover:scale-125 transition-transform">🚀</span>
              <span className="font-semibold text-[11px] text-zinc-400 group-hover:text-cyan-200">
                {reactions.rocket || 0}
              </span>
            </button>

            <button
              type="button"
              onClick={() => handleReact("skull")}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-zinc-900/80 hover:bg-zinc-800 border border-zinc-800 hover:border-purple-500/40 text-xs text-zinc-300 hover:text-purple-300 transition-all active:scale-95 group"
              title="Cultist Sejati 💀"
            >
              <span className="group-hover:scale-125 transition-transform">💀</span>
              <span className="font-semibold text-[11px] text-zinc-400 group-hover:text-purple-200">
                {reactions.skull || 0}
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
